import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ActorRef,
  Audience,
  ClinicAddress,
  ClinicProfileInput,
  ClinicRecord,
  PersonDocument,
} from '@/features/clinic-profile/api'
import type { DocType } from '@/features/clinic-profile/doc-number'
import type { ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'

/**
 * Painel root: clínicas (docs/api/admin.md, "Painel root: clínicas"; RN-ADM-50 a 57 e 85). As rotas
 * são globais de `root` e identificam a clínica no caminho, nunca pela clínica ativa.
 */

/** A clínica do painel: o cadastro mais o bloqueio, a rede e as contagens. */
export interface AdminClinic extends ClinicRecord {
  network: { id: string; name: string } | null
  blockedAt: string | null
  blockedBy: ActorRef | null
  blockJustification: string | null
  counts: { managers: number; users: number }
}

export type ClinicFilter = 'onboarding' | 'active' | 'blocked' | 'no_network'

export interface ClinicListMeta extends ListMeta {
  counts: Record<ClinicFilter, number>
}

export type AccessMode = 'invite' | 'temporary_password'

/** Corpo de `POST /admin/clinics`: o opcional vazio fica de fora (a criação não aceita `null`). */
export interface CreateClinicInput {
  docNumber: string
  docType: DocType
  legalName: string
  tradeName?: string
  address: Omit<ClinicAddress, 'complement'> & { complement?: string }
  phone?: string
  legalEmail: string
  legalRepresentative?: PersonDocument
  timezone: string
  audiences: Audience[]
  /** A rede da clínica, entre as existentes (RN-ADM-54); ausente, nasce sem rede. */
  networkId?: string
  firstManager: { name: string; email: string; accessMode: AccessMode }
}

/**
 * Como a primeira gestora entra; a senha temporária vem uma única vez (RN-ADM-10, RN-ADM-50). Na
 * repetição do mesmo pedido (mesma chave de idempotência) a API devolve a resposta guardada, sem a
 * senha: `temporaryPassword` chega `null`.
 */
export type FirstManagerAccess =
  | { mode: 'invite'; inviteExpiresAt: string; emailSent: boolean }
  | { mode: 'temporary_password'; temporaryPassword: string | null; emailSent: boolean }
  | { mode: 'existing_membership'; emailSent: boolean }

export interface CreatedClinic {
  clinic: AdminClinic
  firstManager: {
    user: { id: string; userId: string; name: string; email: string }
    access: FirstManagerAccess
  }
}

/** Corpo de `PUT /admin/clinics/:id`: o de Empresa mais o que só root edita (RN-ADM-52, 56). */
export interface UpdateClinicInput extends ClinicProfileInput {
  docNumber?: string
  docType?: DocType
  networkId?: string | null
  audiences?: Audience[]
}

export type RegistrationStatus = 'active' | 'suspended' | 'unfit' | 'closed' | 'void'

/** `GET /admin/cnpj/:cnpj`: o que a base pública tem; o que falta volta `null`. */
export interface CnpjRegistration {
  docType: 'cnpj'
  docNumber: string
  legalName: string
  tradeName: string | null
  address: {
    postalCode: string | null
    street: string | null
    number: string | null
    complement: string | null
    district: string | null
    city: string | null
    state: string | null
  }
  registrationStatus: RegistrationStatus | null
}

export const clinicKeys = {
  all: ['clinics'] as const,
  list: (params: object) => ['clinics', 'list', params] as const,
  detail: (id: string) => ['clinics', 'detail', id] as const,
}

export function useClinics(params: {
  filter: ClinicFilter
  search?: string
  page?: number
  perPage?: number
}) {
  const api = useApi()
  return useQuery({
    queryKey: clinicKeys.list(params),
    queryFn: () =>
      api.list<AdminClinic, ClinicListMeta>('/admin/clinics', {
        filter: params.filter,
        search: params.search,
        page: params.page ?? 1,
        perPage: params.perPage ?? 25,
      }),
    placeholderData: (previous) => previous,
  })
}

export function useClinic(id: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: clinicKeys.detail(id ?? ''),
    queryFn: () => api.get<AdminClinic>(`/admin/clinics/${id}`),
    enabled: Boolean(id),
  })
}

/** Toda escrita devolve a clínica como ficou: o detalhe é atualizado na hora e as listas, refeitas. */
function useStoreClinic() {
  const queryClient = useQueryClient()
  return (clinic: AdminClinic) => {
    queryClient.setQueryData(clinicKeys.detail(clinic.id), clinic)
    return queryClient.invalidateQueries({ queryKey: ['clinics', 'list'] })
  }
}

export function useCreateClinic() {
  const api = useApi()
  const store = useStoreClinic()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: CreateClinicInput) =>
      api.post<CreatedClinic>('/admin/clinics', input, {
        idempotencyKey: idempotency.keyFor(input),
      }),
    onSuccess: (created) => {
      idempotency.reset()
      store(created.clinic)
    },
  })
}

export function useUpdateClinic(id: string) {
  const api = useApi()
  const store = useStoreClinic()
  return useMutation({
    mutationFn: (input: UpdateClinicInput) => api.put<AdminClinic>(`/admin/clinics/${id}`, input),
    onSuccess: store,
  })
}

/** Bloquear exige a justificativa (10 a 500 caracteres), que vai para a trilha (RN-ADM-51). */
export function useBlockClinic() {
  const api = useApi()
  const store = useStoreClinic()
  return useMutation({
    mutationFn: ({ id, justification }: { id: string; justification: string }) =>
      api.post<AdminClinic>(`/admin/clinics/${id}/block`, { justification }),
    onSuccess: store,
  })
}

/** Desbloquear restaura tudo como estava; a justificativa é opcional. */
export function useUnblockClinic() {
  const api = useApi()
  const store = useStoreClinic()
  return useMutation({
    mutationFn: ({ id, justification }: { id: string; justification?: string }) =>
      api.post<AdminClinic>(`/admin/clinics/${id}/unblock`, justification ? { justification } : {}),
    onSuccess: store,
  })
}

/**
 * Consulta de CNPJ pela API (RN-ADM-04): só pré-preenche a criação; a clínica nunca depende dela.
 * A consulta de CEP, que a gestora também usa, é `useLookupPostalCode` em `clinic-profile/api.ts`.
 */
export function useLookupCnpj() {
  const api = useApi()
  return useMutation({
    mutationFn: (cnpj: string) => api.get<CnpjRegistration>(`/admin/cnpj/${cnpj}`),
  })
}
